// src/views/admin/ADPlannifications.jsx
import { useState, useEffect } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import { useNavigate } from "react-router-dom";

function Modal({ title, onClose, children, maxWidth = 560 }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth }}>
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const EMPTY = { nom: "", trimestre: "", sous_trimestre: "", date_debut: "", date_fin: "", est_terminee: false };

export default function ADPlannifications({ slug }) {
  const navigate = useNavigate();
  const [annees, setAnnees] = useState([]);
  const [anneeFilt, setAnneeFilt] = useState("");
  const [trimestres, setTrimestres] = useState([]);
  const [trimestreFilt, setTrimestreFilt] = useState("");
  const [classes, setClasses] = useState([]);

  const [plannifications, setPlannifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY);

  // ── Sélection élèves (transfert gauche/droite) ──
  const [allEleves, setAllEleves] = useState(null);
  const [assignClasseFilt, setAssignClasseFilt] = useState("");
  const [assignSearch, setAssignSearch] = useState("");
  const [pickedEleveIds, setPickedEleveIds] = useState([]);
  const [loadingEleves, setLoadingEleves] = useState(false);
  const [selectedGaucheIds, setSelectedGaucheIds] = useState([]); // cochés côté "disponibles"
  const [selectedDroiteIds, setSelectedDroiteIds] = useState([]); // cochés côté "sélectionnés"

  const toast = useToastHook();
  const {
    getAnneesScolaires, getTrimestres, getClasses, getEleves,
    getPlannifications, createPlannification, updatePlannification,
    deletePlannification, assignElevesPlannification,
  } = useAdminService();

  useEffect(() => {
    if (slug) init();
  }, [slug]);

  useEffect(() => {
    if (slug && anneeFilt) loadData();
  }, [slug, anneeFilt]);

  const init = async () => {
    setLoading(true);
    try {
      const [anneesData, classesData] = await Promise.all([
        getAnneesScolaires(slug, { show_archived: "false" }),
        getClasses(slug, { show_archived: "false" }),
      ]);
      setAnnees(anneesData);
      setClasses(classesData);
      const anneeActive = anneesData.find(a => a.actif);
      setAnneeFilt(String((anneeActive || anneesData[0])?.id || ""));
    } catch (error) {
      toast.error("Impossible de charger les données de référence");
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [trimData, plannifData] = await Promise.all([
        getTrimestres(slug, { annee_scolaire: anneeFilt }),
        getPlannifications(slug, { annee_scolaire: anneeFilt }),
      ]);
      setTrimestres(trimData);
      setPlannifications(plannifData);
    } catch (error) {
      toast.error("Impossible de charger les plannifications");
    }
  };

  const sousTrimestresDe = (trimestreId) => {
    const t = trimestres.find(t => String(t.id) === String(trimestreId));
    return t?.sous_trimestres || [];
  };

  const filtered = plannifications.filter(p => {
    const matchSearch = p.nom?.toLowerCase().includes(search.toLowerCase());
    const matchTrim = !trimestreFilt || String(p.trimestre) === String(trimestreFilt);
    return matchSearch && matchTrim;
  });

  const formatDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString("fr-FR"); } catch { return d; }
  };

  const ensureElevesLoaded = async () => {
    if (allEleves !== null) return;
    setLoadingEleves(true);
    try {
      const data = await getEleves(slug, { page_size: "all" });
      setAllEleves(data.results || data || []);
    } catch (error) {
      toast.error("Impossible de charger la liste des élèves");
      setAllEleves([]);
    } finally {
      setLoadingEleves(false);
    }
  };

  const classesDeAnnee = classes.filter(c => String(c.annee_scolaire) === String(anneeFilt));

  const eleveClasseId = (eleve) => {
    const insc = (eleve.inscriptions || []).find(i => String(i.annee_scolaire) === String(anneeFilt));
    return insc?.classe ?? null;
  };
  const eleveClasseNom = (eleve) => {
    const insc = (eleve.inscriptions || []).find(i => String(i.annee_scolaire) === String(anneeFilt));
    return insc?.classe_nom ?? "Non inscrit";
  };

  // ── Classes concernées par une plannification (déduites des élèves assignés) ──
  const getClassesConcernees = (p) => {
    const elevesDetail = p.eleves_concernes_detail || [];
    if (elevesDetail.length === 0) return [];
    const noms = new Set(
      elevesDetail.map(e => e.classe_nom ?? eleveClasseNom(e)).filter(Boolean)
    );
    return Array.from(noms);
  };

  // ── Listes dérivées pour le transfert (disponibles / sélectionnés) ──
  const matchAssignFilters = (e) => {
    const matchClasse = !assignClasseFilt || String(eleveClasseId(e)) === String(assignClasseFilt);
    const matchSearch = !assignSearch ||
      `${e.prenom} ${e.nom}`.toLowerCase().includes(assignSearch.toLowerCase()) ||
      e.matricule?.toLowerCase().includes(assignSearch.toLowerCase());
    return matchClasse && matchSearch;
  };

  const pickedSet = new Set(pickedEleveIds);

  const elevesDisponibles = (allEleves || [])
    .filter(e => !pickedSet.has(e.id))
    .filter(matchAssignFilters);

  const elevesSelectionnes = (allEleves || []).filter(e => pickedSet.has(e.id));

  const toggleGauche = (id) => {
    setSelectedGaucheIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleDroite = (id) => {
    setSelectedDroiteIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSelectAllGauche = () => {
    const ids = elevesDisponibles.map(e => e.id);
    setSelectedGaucheIds(prev => prev.length === ids.length ? [] : ids);
  };
  const handleSelectAllDroite = () => {
    const ids = elevesSelectionnes.map(e => e.id);
    setSelectedDroiteIds(prev => prev.length === ids.length ? [] : ids);
  };

  const handleTransferRight = () => {
    if (selectedGaucheIds.length === 0) return;
    setPickedEleveIds(prev => Array.from(new Set([...prev, ...selectedGaucheIds])));
    setSelectedGaucheIds([]);
  };

  const handleTransferLeft = () => {
    if (selectedDroiteIds.length === 0) return;
    const removeSet = new Set(selectedDroiteIds);
    setPickedEleveIds(prev => prev.filter(id => !removeSet.has(id)));
    setSelectedDroiteIds([]);
  };

  const resetTransfertState = () => {
    setAssignClasseFilt("");
    setAssignSearch("");
    setSelectedGaucheIds([]);
    setSelectedDroiteIds([]);
  };

  // ── CRUD ──
  const openCreate = async () => {
    setForm({ ...EMPTY, trimestre: trimestres[0]?.id || "" });
    setPickedEleveIds([]);
    resetTransfertState();
    setModal("create");
    await ensureElevesLoaded();
  };

  const openEdit = async (p) => {
    setSelected(p);
    setForm({
      nom: p.nom,
      trimestre: p.trimestre,
      sous_trimestre: p.sous_trimestre || "",
      date_debut: p.date_debut || "",
      date_fin: p.date_fin || "",
      est_terminee: p.est_terminee,
    });
    setPickedEleveIds(p.eleves_concernes || []);
    resetTransfertState();
    setModal("edit");
    await ensureElevesLoaded();
  };

  const openDelete = (p) => { setSelected(p); setModal("delete"); };

  const handleFormChange = (field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === "trimestre") next.sous_trimestre = "";
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.nom.trim()) { toast.error("Le nom est requis"); return; }
    if (!form.trimestre) { toast.error("Le trimestre est requis"); return; }
    setSaving(true);
    try {
      const dataToSend = {
        nom: form.nom.trim(),
        trimestre: form.trimestre,
        sous_trimestre: form.sous_trimestre || null,
        date_debut: form.date_debut || null,
        date_fin: form.date_fin || null,
        est_terminee: form.est_terminee,
        eleves_concernes: pickedEleveIds,
      };
      if (modal === "create") {
        const created = await createPlannification(slug, dataToSend);
        setPlannifications(prev => [...prev, created]);
        toast.success(`Plannification "${created.nom}" créée avec succès (${pickedEleveIds.length} élève(s))`);
      } else {
        const updated = await updatePlannification(slug, selected.id, dataToSend);
        setPlannifications(prev => prev.map(p => p.id === selected.id ? updated : p));
        toast.success(`Plannification "${updated.nom}" modifiée avec succès`);
      }
      setModal(null);
    } catch (error) {
      const data = error.response?.data;
      toast.error(typeof data === "object" ? Object.values(data).flat().join("\n") : "Une erreur est survenue");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deletePlannification(slug, selected.id);
      setPlannifications(prev => prev.filter(p => p.id !== selected.id));
      toast.success(`Plannification "${selected.nom}" supprimée avec succès`);
      setModal(null);
    } catch (error) {
      toast.error("Impossible de supprimer cette plannification");
    } finally {
      setSaving(false);
    }
  };

  const openAssign = async (p) => {
    setSelected(p);
    setPickedEleveIds(p.eleves_concernes || []);
    resetTransfertState();
    setModal("assign");
    await ensureElevesLoaded();
  };

  const handleSaveAssign = async () => {
    setSaving(true);
    try {
      const updated = await assignElevesPlannification(slug, selected.id, {
        eleves_concernes: pickedEleveIds,
        mode: "replace",
      });
      setPlannifications(prev => prev.map(p => p.id === selected.id ? updated : p));
      toast.success(`${pickedEleveIds.length} élève(s) assigné(s) avec succès`);
      setModal(null);
    } catch (error) {
      const data = error.response?.data;
      const msg = typeof data === "object"
        ? Object.values(data).flat().join("\n")
        : "Impossible de mettre à jour les élèves assignés";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // ─── Rendu du picker d'élèves (transfert gauche/droite) ──
  const renderElevesPicker = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <select
          value={assignClasseFilt}
          onChange={e => setAssignClasseFilt(e.target.value)}
          className="form-control"
          style={{ flex: 1, minWidth: 140, padding: "8px 12px", borderRadius: 5, border: "0.5px solid var(--b2)" }}
        >
          <option value="">Toutes les classes</option>
          {classesDeAnnee.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
        </select>
        <input
          type="text"
          placeholder="Rechercher…"
          value={assignSearch}
          onChange={e => setAssignSearch(e.target.value)}
          className="form-control"
          style={{ flex: 1, minWidth: 140, padding: "8px 12px", borderRadius: 5, border: "0.5px solid var(--b2)" }}
        />
      </div>

      {loadingEleves ? (
        <Loader />
      ) : (
        <div className="transfer-wrap">
          {/* Colonne disponibles */}
          <div className="transfer-col">
            <div className="transfer-col-head">
              <span className="transfer-col-title ok">Disponibles ({elevesDisponibles.length})</span>
              {elevesDisponibles.length > 0 && (
                <button type="button" className="transfer-select-all ok" onClick={handleSelectAllGauche}>
                  {selectedGaucheIds.length === elevesDisponibles.length ? "Désélectionner" : "Tout sélectionner"}
                </button>
              )}
            </div>
            <div className="transfer-list">
              {elevesDisponibles.length === 0 ? (
                <div className="transfer-empty">Aucun élève disponible</div>
              ) : (
                elevesDisponibles.map(e => (
                  <div
                    key={e.id}
                    className={`transfer-item ${selectedGaucheIds.includes(e.id) ? "picked" : ""}`}
                    onClick={() => toggleGauche(e.id)}
                  >
                    <div className="transfer-item-name">{e.prenom} {e.nom}</div>
                    <div className="transfer-item-meta">
                      {e.matricule} <span className="pill neu">{eleveClasseNom(e)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Flèches */}
          <div className="transfer-arrows">
            <button
              type="button"
              className="ibtn arrow-add"
              disabled={selectedGaucheIds.length === 0}
              onClick={handleTransferRight}
              title="Ajouter les élèves cochés"
            >
              <i className="ti ti-arrow-right" />
            </button>
            <button
              type="button"
              className="ibtn arrow-remove"
              disabled={selectedDroiteIds.length === 0}
              onClick={handleTransferLeft}
              title="Retirer les élèves cochés"
            >
              <i className="ti ti-arrow-left" />
            </button>
          </div>

          {/* Colonne sélectionnés */}
          <div className="transfer-col">
            <div className="transfer-col-head">
              <span className="transfer-col-title rd">Sélectionnés ({elevesSelectionnes.length})</span>
              {elevesSelectionnes.length > 0 && (
                <button type="button" className="transfer-select-all rd" onClick={handleSelectAllDroite}>
                  {selectedDroiteIds.length === elevesSelectionnes.length ? "Désélectionner" : "Tout sélectionner"}
                </button>
              )}
            </div>
            <div className="transfer-list">
              {elevesSelectionnes.length === 0 ? (
                <div className="transfer-empty">Aucun élève sélectionné</div>
              ) : (
                elevesSelectionnes.map(e => (
                  <div
                    key={e.id}
                    className={`transfer-item ${selectedDroiteIds.includes(e.id) ? "picked" : ""}`}
                    onClick={() => toggleDroite(e.id)}
                  >
                    <div className="transfer-item-name">{e.prenom} {e.nom}</div>
                    <div className="transfer-item-meta">
                      {e.matricule} <span className="pill neu">{eleveClasseNom(e)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{ fontSize: 13, color: "var(--m)" }}>
        {pickedEleveIds.length} élève(s) sélectionné(s) au total
      </div>
    </div>
  );

  if (loading) return <Loader />;

  return (
    <div>
      {/* Barre d'outils */}
      <div className="sb2">
        <input type="text" placeholder="Rechercher une plannification…" value={search} onChange={e => setSearch(e.target.value)} />
        <select value={anneeFilt} onChange={e => setAnneeFilt(e.target.value)}>
          {annees.map(a => <option key={a.id} value={a.id}>{a.nom}{a.actif ? " (active)" : ""}</option>)}
        </select>
        <select value={trimestreFilt} onChange={e => setTrimestreFilt(e.target.value)}>
          <option value="">Tous les trimestres</option>
          {trimestres.map(t => <option key={t.id} value={t.id}>{t.nom}</option>)}
        </select>
        <button className="bsec" onClick={loadData}><i className="ti ti-refresh" />Actualiser</button>
        <button className="bprim" onClick={openCreate} disabled={trimestres.length === 0}>
          <i className="ti ti-plus" />Nouvelle plannification
        </button>
      </div>

      {trimestres.length === 0 && (
        <div style={{ fontSize: 13, color: "var(--h)", marginBottom: 10 }}>
          Aucun trimestre pour cette année scolaire — crée d'abord un trimestre.
        </div>
      )}

      {/* Cartes des plannifications - comme l'ancien */}
      <div className="cards-grid">
        {filtered.map(p => {
          const elevesCount = (p.eleves_concernes_detail || p.eleves_concernes || []).length;
          const classesConcernees = getClassesConcernees(p);
          return (
            <div key={p.id} className={`planif-card ${p.est_terminee ? "terminée" : ""}`}>
              <div className="planif-card-header">
                <div>
                  <h4 className="planif-card-title">
                    {p.nom}
                    {classesConcernees.length === 1 && (
                      <span className="pill neu" title={classesConcernees[0]}>
                        {classesConcernees[0]}
                      </span>
                    )}
                    {classesConcernees.length > 1 && (
                      <span className="pill neu" title={classesConcernees.join(", ")}>
                        {classesConcernees.length} classes
                      </span>
                    )}
                    {p.est_terminee && <span className="pill ok">Terminée</span>}
                  </h4>
                  <div className="planif-card-subtitle">
                    {p.trimestre_detail?.nom}
                    {p.sous_trimestre_detail?.nom && ` · ${p.sous_trimestre_detail.nom}`}
                  </div>
                </div>
                <div className="planif-card-actions">
                  <button
                    className="bsec"
                    onClick={() => openAssign(p)}
                    title="Assigner des élèves"
                    style={{ padding: "6px 10px" }}
                  >
                    <i className="ti ti-users" />
                  </button>
                  <button
                    className="bsec"
                    onClick={() => openEdit(p)}
                    title="Modifier"
                    style={{ padding: "6px 10px", background: "#ffc107", color: "black", border: "none" }}
                  >
                    <i className="ti ti-edit" />
                  </button>
                  <button
                    className="bsec del"
                    onClick={() => openDelete(p)}
                    title="Supprimer"
                    style={{ padding: "6px 10px" }}
                  >
                    <i className="ti ti-trash" />
                  </button>
                </div>
              </div>

              <div className="planif-card-period">
                <strong>Période :</strong>
                {p.date_debut && p.date_fin
                  ? `${formatDate(p.date_debut)} — ${formatDate(p.date_fin)}`
                  : "Période non définie"}
              </div>

              <div className="planif-card-eleves">
                <strong>Élèves concernés :</strong> {elevesCount} élève(s)
              </div>

              <div className="planif-card-buttons">
                <button
                  className="bprim"
                  onClick={() => navigate(`/${slug}/admin/notes?plannification=${p.id}`)}
                  style={{ background: "#28a745" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#1e7e34"}
                  onMouseLeave={e => e.currentTarget.style.background = "#28a745"}
                >
                  <i className="ti ti-notebook" /> Saisir les notes
                </button>
                <button
                  className="bprim"
                  onClick={() => navigate(`/${slug}/admin/bulletins?plannification=${p.id}`)}
                  style={{ background: "#17a2b8" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#138496"}
                  onMouseLeave={e => e.currentTarget.style.background = "#17a2b8"}
                >
                  <i className="ti ti-file-certificate" /> Voir les bulletins
                </button>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="card" style={{ gridColumn: "1/-1", textAlign: "center", color: "var(--h)", padding: 40 }}>
            {search || trimestreFilt ? "Aucune plannification ne correspond à votre recherche." : "Aucune plannification créée."}
          </div>
        )}
      </div>

      {/* ─── MODALS ─── */}

      {/* Modal Créer/Modifier */}
      {(modal === "create" || modal === "edit") && (
        <Modal title={modal === "create" ? "Nouvelle plannification" : "Modifier la plannification"} onClose={() => setModal(null)} maxWidth={740}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fg"><label>Nom *</label><input value={form.nom} onChange={e => handleFormChange('nom', e.target.value)} placeholder="Ex: Composition du 1er trimestre" /></div>
            <div className="fg">
              <label>Trimestre *</label>
              <select value={form.trimestre} onChange={e => handleFormChange('trimestre', e.target.value)}>
                <option value="">-- Sélectionner --</option>
                {trimestres.map(t => <option key={t.id} value={t.id}>{t.nom}</option>)}
              </select>
            </div>
            <div className="fg">
              <label>Sous-trimestre (optionnel)</label>
              <select value={form.sous_trimestre} onChange={e => handleFormChange('sous_trimestre', e.target.value)} disabled={!form.trimestre}>
                <option value="">-- Aucun --</option>
                {sousTrimestresDe(form.trimestre).map(st => <option key={st.id} value={st.id}>{st.nom}</option>)}
              </select>
            </div>
            <div className="fgrid">
              <div className="fg"><label>Date début</label><input type="date" value={form.date_debut} onChange={e => handleFormChange('date_debut', e.target.value)} /></div>
              <div className="fg"><label>Date fin</label><input type="date" value={form.date_fin} onChange={e => handleFormChange('date_fin', e.target.value)} /></div>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
              <input type="checkbox" checked={form.est_terminee} onChange={e => handleFormChange('est_terminee', e.target.checked)} />
              Plannification terminée
            </label>

            <div style={{ borderTop: "0.5px solid var(--b)", paddingTop: 10, marginTop: 4 }}>
              <label style={{ fontSize: 14, color: "var(--m)", fontWeight: 500, display: "block", marginBottom: 6 }}>
                Élèves concernés
              </label>
              {renderElevesPicker()}
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSave} disabled={saving}>
              {saving ? "En cours..." : <><i className="ti ti-check" /> {modal === "create" ? "Créer" : "Enregistrer"}</>}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Supprimer */}
      {modal === "delete" && selected && (
        <Modal title="Confirmer la suppression" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)" }}>
            Supprimer <strong>{selected.nom}</strong> ?
            <div style={{ color: "var(--rd)", marginTop: 8 }}>⚠️ Les notes et bulletins liés seront supprimés aussi.</div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDelete} disabled={saving}>
              {saving ? "..." : <><i className="ti ti-trash" />Supprimer</>}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Assignation */}
      {modal === "assign" && selected && (
        <Modal title={`Élèves concernés — ${selected.nom}`} onClose={() => setModal(null)} maxWidth={740}>
          {renderElevesPicker()}
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSaveAssign} disabled={saving}>
              {saving ? "..." : "Enregistrer"}
            </button>
          </div>
        </Modal>
      )}

      <style>{`
        /* ─── CARTES PLANIFICATION ─── */
        .cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
          gap: 16px;
          margin-top: 12px;
        }

        .planif-card {
          background: var(--s);
          border: 0.5px solid var(--b);
          border-radius: var(--radius-md);
          padding: 16px 18px;
          transition: all 0.2s;
        }

        .planif-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          transform: translateY(-2px);
        }

        .planif-card.terminée {
          background: var(--gn-bg);
          border-color: var(--gn);
        }

        .planif-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 10px;
        }

        .planif-card-title {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: var(--t);
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 6px;
        }

        .planif-card-title .pill {
          font-size: 11px;
          font-weight: 500;
        }

        .planif-card-subtitle {
          font-size: 13px;
          color: var(--m);
          margin-top: 2px;
        }

        .planif-card-actions {
          display: flex;
          gap: 4px;
          flex-shrink: 0;
        }

        .planif-card-actions button {
          width: 30px;
          height: 30px;
          border-radius: 4px;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          transition: all .2s;
          background: var(--s2);
          color: var(--m);
        }

        .planif-card-actions button:hover {
          opacity: 0.8;
        }

        .planif-card-actions button.del {
          background: var(--rd);
          color: white;
        }

        .planif-card-actions button.del:hover {
          background: var(--rd-dk);
        }

        .planif-card-period {
          font-size: 14px;
          color: var(--m);
          margin-bottom: 6px;
        }

        .planif-card-eleves {
          font-size: 14px;
          color: var(--m);
          margin-bottom: 12px;
        }

        .planif-card-buttons {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .planif-card-buttons .bprim {
          flex: 1;
          justify-content: center;
          min-width: 120px;
        }

        /* ─── ONGLETS ─── */
        .tab-nav {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
          background: var(--pr);
          border-radius: 10px;
          padding: 8px 12px;
          margin-bottom: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .tab-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 18px;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          background: transparent;
          color: rgba(255,255,255,0.7);
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        .tab-btn i { font-size: 18px; }
        .tab-btn:hover { background: rgba(255,255,255,0.15); color: white; }
        .tab-btn.active { background: white; color: var(--pr); box-shadow: 0 2px 8px rgba(0,0,0,0.15); }
        .tab-btn.active:hover { background: white; color: var(--pr); }

        .fgrid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 9px;
        }

        /* ─── TRANSFERT ÉLÈVES (disponibles ↔ sélectionnés) ─── */
        .transfer-wrap {
          display: flex;
          align-items: stretch;
          gap: 10px;
        }

        .transfer-col {
          flex: 1;
          min-width: 0;
          border: 0.5px solid var(--b);
          border-radius: 8px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .transfer-col-head {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 6px;
          padding: 8px 10px;
          background: var(--s2);
          border-bottom: 0.5px solid var(--b);
          flex-wrap: wrap;
          row-gap: 4px;
        }

        .transfer-col-title {
          font-size: 12px;
          font-weight: 700;
          white-space: nowrap;
        }

        .transfer-col-title.ok { color: var(--gn); }
        .transfer-col-title.rd { color: var(--rd); }

        .transfer-select-all {
          background: transparent;
          border: none;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          padding: 2px 6px;
          border-radius: 4px;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .transfer-select-all.ok { color: var(--gn); }
        .transfer-select-all.rd { color: var(--rd); }
        .transfer-select-all:hover { background: var(--s); }

        .transfer-list {
          max-height: 260px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
        }

        .transfer-empty {
          padding: 16px 10px;
          text-align: center;
          font-size: 12px;
          color: var(--h);
        }

        .transfer-item {
          padding: 7px 10px;
          cursor: pointer;
          border-bottom: 0.5px solid var(--b);
          font-size: 13px;
        }

        .transfer-item:last-child { border-bottom: none; }
        .transfer-item:hover { background: var(--s2); }
        .transfer-item.picked { background: var(--pr-bg, rgba(0,64,128,0.08)); }

        .transfer-item-name { font-weight: 600; color: var(--t); }
        .transfer-item-meta { font-size: 11px; color: var(--m); display: flex; align-items: center; gap: 6px; margin-top: 2px; }

        .transfer-arrows {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }

        .transfer-arrows .ibtn {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          border: 0.5px solid var(--b2);
          background: var(--s);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all .2s;
        }

        .transfer-arrows .ibtn:hover:not(:disabled) { background: var(--s2); }
        .transfer-arrows .ibtn:disabled { opacity: 0.4; cursor: not-allowed; }

        .transfer-arrows .ibtn.arrow-add {
          border-color: var(--gn);
          color: var(--gn);
        }
        .transfer-arrows .ibtn.arrow-add:hover:not(:disabled) {
          background: var(--gn);
          color: white;
        }

        .transfer-arrows .ibtn.arrow-remove {
          border-color: var(--rd);
          color: var(--rd);
        }
        .transfer-arrows .ibtn.arrow-remove:hover:not(:disabled) {
          background: var(--rd);
          color: white;
        }

        @media (max-width: 768px) {
          .tab-btn { padding: 6px 10px; font-size: 11px; }
          .tab-btn i { font-size: 14px; }
          .fgrid { grid-template-columns: 1fr; }
          .cards-grid { grid-template-columns: 1fr; }
          .transfer-wrap { flex-direction: column; }
          .transfer-arrows { flex-direction: row; }
        }
        @media (max-width: 576px) {
          .tab-btn span { display: none; }
          .tab-btn i { font-size: 18px; margin: 0; }
        }
      `}</style>
    </div>
  );
}